// Shape compartilhada entre server e client para o feature alpha
// "Inteligência de Candidatos" (labs/candidate-intelligence).
// v1: payload é retornado a partir de um mock fixo no backend. Quando o pipeline
// real (parsing de PDF do LinkedIn + LLM) entrar no ar, o backend produz este
// mesmo shape e o frontend continua funcionando sem alteração.

import type { ThreeAxisRecommendation } from "./three-axis";

export type Flag = "green" | "yellow" | "red";

export type CareerQuality = "exceptional" | "high" | "good";

export type SkillGap = "none" | "minor" | "moderate" | "notable";

export type InsightVerdict = "exceptional" | "good" | "high" | "moderate" | "cleared" | "flag";

export type ClassificationCategory = "academic" | "companies" | "movements" | "skills" | "referral";

export interface CareerEntry {
  company: string;
  role: string;
  period: string;
  duration: string;
  moment: string;
  growth: string;
  quality: CareerQuality;
  promoted: boolean;
  momentScore: number;
}

export interface Criterion {
  name: string;
  flag: Flag;
  flagLabel: string;
  weight: number;
  score: number;
  signals: string[];
  /** Why this score and not higher/lower — what's pushing it up or dragging it down. */
  rationale?: string;
}

export interface RawSource {
  company: string;
  role: string;
  period: string;
  description: string;
}

export interface ObservationFlag {
  text: string;
  quote?: string;
  citation?: string;
  gapRationale?: string;
}

export interface PositionSkill {
  id: string;
  name: string;
  tagline: string;
  required: number;
  requiredLabel: string;
  assessed: number;
  assessedLabel: string;
  gap: SkillGap;
  levelMins: Record<string, number>;
  evidence: string;
  greenFlags?: ObservationFlag[];
  redFlags?: ObservationFlag[];
  /** Candidate's observed proficiency on a 0–5 scale, independent of the
   *  L{1..5} bucket in `assessed`. Set this when the candidate exceeds the
   *  framework's authored score for the assigned level (e.g., HR Fluency
   *  caps at 3/5 at L5, but a seasoned HR executive can be 4–5/5). When
   *  present, it drives the radar directly; otherwise the radar falls back
   *  to the framework's authored score for the assigned level. */
  assessedDots?: number;
}

export interface Position {
  team: string;
  goal: string;
  targetLevel: string;
  skills: PositionSkill[];
}

export interface Education {
  institution: string;
  degree: string;
  graduated: number;
  tier: string;
  flag: Flag;
  notes: string;
}

export interface ClassificationAcademic {
  institution: string;
  degree: string;
  field: string;
  year: number;
  tier: string;
}

export interface ClassificationCompany {
  company: string;
  entry: string;
  exit: string;
  duration: string;
  stage: string;
  era: string;
}

export interface ClassificationMovement {
  move: string;
  type: string;
  timing: string;
  direction: string;
}

export interface ClassificationSkill {
  company: string;
  responsibility: string;
  scope: string;
}

export interface ClassificationReferral {
  referee: string;
  seniority: string;
  relationship: string;
  duration: string;
}

export interface Classification {
  academic: ClassificationAcademic[];
  companies: ClassificationCompany[];
  movements: ClassificationMovement[];
  skills: ClassificationSkill[];
  referral: ClassificationReferral[];
}

export interface Insight {
  id: string;
  label: string;
  verdict: InsightVerdict;
  verdictLabel: string;
  /** Flag semafórico para colorir o badge do veredito. */
  flag?: Flag;
  derivedFrom: ClassificationCategory[];
  /** Sinais explícitos — evidência direta do perfil. */
  explicitSignals: string[];
  /** Sinais implícitos — inferências derivadas de padrões cruzados. */
  implicitSignals: string[];
}

export interface SourceEntry {
  source: string;
  categories: string[];
}

export interface OntologyEntry {
  category: string;
  dimensions: string[];
}

/**
 * Input for the market-level inference primitive.
 *
 * `era` is deliberately NOT in `ClassificationCompanyForLevel` — it is a
 * derived quality score (Alto/Moderado/Excepcional/Baixo) produced by an
 * upstream LLM, not a scope signal. Including it would create a feedback
 * loop where the level prompt double-counts a quality judgment.
 */
export type ClassificationCompanyForLevel = Pick<
  ClassificationCompany,
  "company" | "stage" | "duration" | "entry" | "exit"
>;

export interface MarketLevelInput {
  career: CareerEntry[];
  rawSources: RawSource[];
  classificationCompanies: ClassificationCompanyForLevel[];
}

export interface MarketLevelResult {
  marketLevel: string; // display_name from MARKET_LEVELS
  cglDisplayName: string; // "L3 - IC", derived server-side
  confidence: number; // 0.0–1.0
  reasoning: string; // 2–4 sentences in Portuguese
}

export interface CandidateAssessment {
  candidateName: string;
  linkedinUrl: string;
  referralFrom: string;
  referralRole: string;
  referralNote: string;
  /**
   * Pass/fail label summarising the three-axis decision (e.g. "Hire",
   * "Leaning hire", "Do not advance"). Replaces the old 0–1 `overallScore`
   * and `fitLabel` pair, which composed sub-axis scores into a single
   * number. The three-axis aggregator is now the authoritative output.
   */
  percentile: string;
  recommendedLevel: string;
  appliedLevel: string;
  levelRationale: string;
  education: Education;
  summary: string;
  career: CareerEntry[];
  criteria: Criterion[];
  rawSources: RawSource[];
  position: Position;
  classification: Classification;
  insights: Insight[];
  sources: SourceEntry[];
  ontology: OntologyEntry[];
  /** Three-axis recommendation derived from criteria + positionFit. */
  threeAxis?: ThreeAxisRecommendation;
  marketLevel?: MarketLevelResult;
}

export interface AssessCandidateInput {
  candidateName?: string;
  linkedinUrl?: string;
  referral?: {
    from?: string;
    role?: string;
    note?: string;
  } | null;
  profilePdf?: {
    fileName: string;
    fileContent: string; // base64
    fileType?: string;
    fileSize?: number;
  };
  position: {
    discipline: string; // slug: "forward-deployed"
    level?: string; // "L4" — optional; recommended by the model when omitted
    additionalContext?: string; // optional free text
  };
}

/** Resultado completo da avaliação LLM — espelha subconjunto de CandidateAssessment. */
export interface LLMEvaluation {
  // Call 1: classification + insights + recommendation
  classification: Classification;
  insights: Insight[];
  sources: SourceEntry[];
  ontology: OntologyEntry[];
  /**
   * Level + rationale only. The legacy 0–1 `overallScore`,
   * `hardSkillsScore`, `cultureGcaScore`, and `fitLabel` composites are
   * no longer used — the three-axis aggregator (below) is the only
   * authoritative recommendation.
   */
  recommendation: {
    recommendedLevel: string;
    levelRationale: string;
  };
  // Call 2: profile extraction (parallel with call 1)
  candidateName?: string;
  career?: CareerEntry[];
  education?: Education;
  rawSources?: RawSource[];
  // Call 3: scoring (depends on call 1)
  criteria?: Criterion[];
  percentile?: string;
  summary?: string;
  // Call 4: position fit (depends on call 1 + framework)
  positionFit?: {
    team: string;
    targetLevel: string;
    recommendedLevel: string;
    levelRationale: string;
    skills: PositionSkill[];
  };
  /** Three-axis recommendation derived from criteria + positionFit. */
  threeAxis?: ThreeAxisRecommendation;
  marketLevel?: MarketLevelResult;
}

export interface AssessCandidateResponse {
  assessment: CandidateAssessment;
  /** Indica que o payload é o demo fixo (v1 alpha). */
  mocked: boolean;
  /** Quando um PDF é enviado, devolvemos um preview do texto extraído para feedback visual. */
  extractedTextPreview?: string;
  extractedCharCount?: number;
  /** Avaliação completa gerada pelo LLM. */
  llmEvaluation?: LLMEvaluation | null;
  /** Se a avaliação LLM falhou, o motivo é retornado aqui. */
  llmEvaluationError?: string;
  /** ID da linha recém-criada em `labs_evaluations`. O cliente usa este id
   *  para promover o perfil avaliado ao corpus de calibração direto da tela
   *  de relatório (POST /api/labs/evaluations/:id/promote-to-fixture). */
  archivedEvaluationId?: string;
}

/**
 * Minimal placeholder assessment — NOT real data.
 * Used only as a type-safe default before LLM results arrive.
 * If you see these values in the UI, something is wrong with the data pipeline.
 */
export const DEMO_CANDIDATE_ASSESSMENT: CandidateAssessment = {
  candidateName: "Candidato",
  linkedinUrl: "",
  referralFrom: "",
  referralRole: "",
  referralNote: "",
  percentile: "—",
  recommendedLevel: "—",
  appliedLevel: "—",
  levelRationale: "",
  education: {
    institution: "—",
    degree: "—",
    graduated: 0,
    tier: "unknown",
    flag: "yellow",
    notes: "",
  },
  summary: "",
  career: [],
  criteria: [],
  rawSources: [],
  position: {
    team: "—",
    goal: "—",
    targetLevel: "—",
    skills: [],
  },
  classification: {
    academic: [],
    companies: [],
    movements: [],
    skills: [],
    referral: [],
  },
  insights: [],
  sources: [],
  ontology: [],
};
